import type { Row, SortingState } from '@tanstack/react-table';
import {
  compareEmptyLast,
  isEmptyNumericSortValue,
  isEmptySortValue,
  statusSort,
} from '../../../../utilities/customSorting';
import {
  tanStackCustomColumnSort,
  tanStackSortOptions,
  type TanStackSortOptions,
} from '../../../tables/tanstack/tanstackSorting';

/** @deprecated Use {@link tanStackCustomColumnSort} from `tables/tanstack/tanstackSorting`. */
export const navReconCustomColumnSort = tanStackCustomColumnSort;

/** @deprecated Use {@link tanStackCustomColumnSort}. */
export const navReconStatusColumnSort = navReconCustomColumnSort;

export const navReconNumericColumnSort = navReconCustomColumnSort;

export type NavReconRowWithPayload = { overviewHasPayload: boolean };

/** Client audit column: pending → rejected → approved; empty last. */
function sortNavReconClientAuditStatus(
  a: string | null | undefined,
  b: string | null | undefined,
  options: TanStackSortOptions,
): number {
  const emptyCompare = compareEmptyLast(
    isEmptySortValue(a),
    isEmptySortValue(b),
    options,
  );
  if (emptyCompare !== 0) return emptyCompare;

  const rank: Record<string, number> = {
    pending: 1,
    rejected: 2,
    approved: 3,
  };
  const ar = a == null ? 99 : (rank[a] ?? 99);
  const br = b == null ? 99 : (rank[b] ?? 99);
  return ar - br;
}

export function compareNavReconShellRowsLast<T extends NavReconRowWithPayload>(
  a: T,
  b: T,
  options: TanStackSortOptions,
): number | null {
  const aShell = !a.overviewHasPayload;
  const bShell = !b.overviewHasPayload;
  if (!aShell && !bShell) return null;
  return compareEmptyLast(aShell, bShell, options);
}

export function navReconMetricStatusSortFn<T extends NavReconRowWithPayload & { fund_name?: string }>(
  rowA: Row<T>,
  rowB: Row<T>,
  columnId: string,
  statusKey: keyof T & string,
  getSorting: () => SortingState,
): number {
  const options = tanStackSortOptions(getSorting(), columnId);

  const shellCompare = compareNavReconShellRowsLast(rowA.original, rowB.original, options);
  if (shellCompare !== null && shellCompare !== 0) {
    return shellCompare;
  }

  return statusSort(rowA.original, rowB.original, statusKey, undefined, options);
}

export function navReconAuditStatusSortFn<
  T extends NavReconRowWithPayload & {
    fund_name?: string;
    auditStatus: string | null | undefined;
  },
>(
  rowA: Row<T>,
  rowB: Row<T>,
  columnId: string,
  getSorting: () => SortingState,
): number {
  const options = tanStackSortOptions(getSorting(), columnId);

  const shellCompare = compareNavReconShellRowsLast(rowA.original, rowB.original, options);
  if (shellCompare !== null && shellCompare !== 0) {
    return shellCompare;
  }

  return sortNavReconClientAuditStatus(
    rowA.original.auditStatus,
    rowB.original.auditStatus,
    options,
  );
}

export function navReconNumericSortFn<T extends NavReconRowWithPayload>(
  rowA: Row<T>,
  rowB: Row<T>,
  columnId: string,
  numericKey: keyof T & string,
  getSorting: () => SortingState,
): number {
  const options = tanStackSortOptions(getSorting(), columnId);

  const shellCompare = compareNavReconShellRowsLast(rowA.original, rowB.original, options);
  if (shellCompare !== null && shellCompare !== 0) {
    return shellCompare;
  }

  const aValue = rowA.original[numericKey];
  const bValue = rowB.original[numericKey];

  const emptyCompare = compareEmptyLast(
    isEmptyNumericSortValue(aValue),
    isEmptyNumericSortValue(bValue),
    options,
  );
  if (emptyCompare !== 0) return emptyCompare;

  return (aValue as number) - (bValue as number);
}

/** Asc = less recent (oldest NAV first); desc = most recent first. */
export function navReconNavReceivedSortFn<
  T extends NavReconRowWithPayload & {
    navReceivedTimestamp: string | null;
    navReceivedAtMs: number | null;
    calculationAtMs?: number | null;
  },
>(
  rowA: Row<T>,
  rowB: Row<T>,
  columnId: string,
  getSorting: () => SortingState,
): number {
  const options = tanStackSortOptions(getSorting(), columnId);

  const shellCompare = compareNavReconShellRowsLast(rowA.original, rowB.original, options);
  if (shellCompare !== null && shellCompare !== 0) {
    return shellCompare;
  }

  const aMs =
    columnId === 'calculationTimestamp'
      ? (rowA.original.calculationAtMs ?? null)
      : rowA.original.navReceivedAtMs;
  const bMs =
    columnId === 'calculationTimestamp'
      ? (rowB.original.calculationAtMs ?? null)
      : rowB.original.navReceivedAtMs;

  const emptyCompare = compareEmptyLast(
    isEmptyNumericSortValue(aMs),
    isEmptyNumericSortValue(bMs),
    options,
  );
  if (emptyCompare !== 0) return emptyCompare;

  return (aMs as number) - (bMs as number);
}
